/**
 * AdminAuthContext.tsx — Authentication context for the billing admin console
 *
 * Purpose:
 *   Provides JWT-based authentication state and actions to all admin console
 *   components. The JWT is stored in sessionStorage (cleared on tab close).
 *   Exposes useAdminAuth() hook for consuming auth state.
 *
 * Public API:
 *   - AdminAuthProvider   React context provider — wrap the app root
 *   - useAdminAuth()      Hook returning { user, token, login, logout }
 *   - AdminUser           Type for decoded JWT claims
 *
 * Architecture Context:
 *   HITL resolution: JWT admin role check = claims.role === 'admin' (string).
 *   Token persisted to sessionStorage under key 'billing_admin_token'.
 *   AdminAuthGuard reads this context to protect routes.
 *
 * @module packages/billing-admin/src/contexts/AdminAuthContext
 * @since 2026-05-31 (AT-50.01 — Scaffold Admin Console UI Foundation)
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  clearBootstrapTokenFromUrl,
  getBootstrapToken,
  handleOidcCallback,
  logoutFromOidc,
  persistSession,
  readStoredSession,
  resolveBillingAdminAuthConfig,
  startOidcLogin,
  type BillingAdminAuthConfig,
  type BillingAdminSession,
} from '../lib/admin-oidc';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Decoded JWT claims for an admin user.
 *
 * @since 2026-05-31 (AT-50.01)
 */
export interface AdminUser {
  /** Subject — typically the user ID. */
  sub: string;
  /** Display name / email. */
  email: string;
  /** Permission slugs emitted in the access token. */
  permissions: string[];
  /**
   * Role string — must equal 'admin' for access.
   * Billing requires explicit `billing.admin` access or `platform.admin`.
   */
  role: string;
  /** Tenant/org ID from JWT claims (org_id or tenant_id). */
  tenantId: string;
}

/**
 * Return value of useAdminAuth().
 *
 * @since 2026-05-31 (AT-50.01)
 */
export interface AdminAuthState {
  /** Decoded user from the active JWT, or null when unauthenticated. */
  user: AdminUser | null;
  /** Raw JWT string from sessionStorage, or null when unauthenticated. */
  token: string | null;
  /**
  * Initiates the OIDC PKCE login flow.
   *
  * @param returnTo - Relative app path to return to after callback
  * @throws {Error} If OIDC discovery/bootstrap fails before redirect
   */
  login: (returnTo?: string, opts?: { prompt?: string }) => Promise<void>;
  /** Completes the OIDC callback exchange and returns the post-login path. */
  completeLogin: () => Promise<string>;
  /** Clears the session and ends the OIDC session when available. */
  logout: () => Promise<void>;
  /** Whether a login request is currently in flight. */
  loading: boolean;
  /** Login error message, or null when no error. */
  error: string | null;
  /** Base URL used for authenticated billing admin API requests. */
  apiBaseUrl: string;
}

/**
 * Props for AdminAuthProvider.
 *
 * @since 2026-05-31 (AT-50.01)
 */
export interface AdminAuthProviderProps {
  /** Base URL of the billing service API (no trailing slash). */
  apiBaseUrl: string;
  /** Router basename used to compute callback URLs. */
  basename?: string;
  /** Optional OIDC overrides for direct auth against rule42-auth. */
  auth?: BillingAdminAuthConfig;
  /** Children to render inside the provider. */
  children: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AdminAuthContext = createContext<AdminAuthState | null>(null);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * decodeJwt — Decodes the payload of a JWT without verification.
 *
 * RATIONALE: The admin console is a client-side SPA; JWT signature
 * verification happens on the server. We decode locally only to extract
 * display claims (email, role). No security-sensitive decisions are made
 * solely on client-decoded claims — the server validates on every API call.
 *
 * @param token - Raw JWT string (header.payload.signature)
 * @returns Decoded payload object, or null if the token is malformed
 * @since 2026-05-31 (AT-50.01)
 */
function decodeJwt(token: string): AdminUser | null {
  try {
    const [, payloadB64] = token.split('.');
    if (!payloadB64) return null;
    // EDGE CASE: atob requires standard base64; JWT uses url-safe base64
    const json = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as Record<string, unknown>;
    const sub = typeof payload.sub === 'string' ? payload.sub : '';
    const email = typeof payload.email === 'string' ? payload.email : '';
    const permissions = Array.isArray(payload.permissions)
      ? payload.permissions.filter((entry): entry is string => typeof entry === 'string')
      : [];
    const orgRole = typeof payload.org_role === 'string' ? payload.org_role : '';
    const tokenRole = typeof payload.role === 'string' ? payload.role : '';
    const role =
      tokenRole === 'admin' ||
      orgRole === 'owner' || orgRole === 'admin' ||
      permissions.includes('billing.admin') || permissions.includes('platform.admin')
        ? 'admin'
        : '';
    const tenantId = typeof payload.org_id === 'string'
      ? payload.org_id
      : typeof payload.tenant_id === 'string'
        ? payload.tenant_id
        : '';
    return { sub, email, permissions, role, tenantId };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * AdminAuthProvider — supplies AdminAuthState to the component tree.
 *
 * Reads an existing token from sessionStorage on mount so a page refresh
 * does not log the operator out within the same browser session.
 *
 * @since 2026-05-31 (AT-50.01)
 */
export function AdminAuthProvider({
  apiBaseUrl,
  basename,
  auth,
  children,
}: AdminAuthProviderProps): React.JSX.Element {
  const bootstrapToken = getBootstrapToken();
  const authConfig = useMemo(
    () => resolveBillingAdminAuthConfig(basename, auth),
    [basename, auth?.clientId, auth?.issuer, auth?.postLogoutUri, auth?.redirectUri],
  );
  const [session, setSession] = useState<BillingAdminSession | null>(() =>
    bootstrapToken
      ? { accessToken: bootstrapToken }
      : readStoredSession(),
  );
  const [user, setUser] = useState<AdminUser | null>(() => {
    const initialToken = bootstrapToken ?? readStoredSession()?.accessToken ?? null;
    return initialToken ? decodeJwt(initialToken) : null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const token = session?.accessToken ?? null;

  // Keep user in sync whenever token changes
  useEffect(() => {
    if (token) {
      setUser(decodeJwt(token));
    } else {
      setUser(null);
    }
  }, [token]);

  useEffect(() => {
    if (session) {
      persistSession(session);
    } else {
      sessionStorage.removeItem('billing_admin_auth_session');
      sessionStorage.removeItem('billing_admin_token');
    }
  }, [session]);

  useEffect(() => {
    if (bootstrapToken) {
      clearBootstrapTokenFromUrl();
    }
  }, [bootstrapToken]);

  /**
   * login — initiate OIDC authorization_code + PKCE.
   */
  const login = useCallback(
    async (returnTo = '/overview', opts?: { prompt?: string }): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        await startOidcLogin(authConfig, returnTo, opts);
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred';
        setError(message);
        setLoading(false);
        throw err;
      }
    },
    [authConfig],
  );

  const completeLogin = useCallback(async (): Promise<string> => {
    setLoading(true);
    setError(null);
    try {
      const { session: nextSession, returnTo } = await handleOidcCallback(authConfig);
      const decoded = decodeJwt(nextSession.accessToken);
      if (!decoded || decoded.role !== 'admin') {
        setSession(null);
        throw new Error('You do not have permission to access rule42 Billing. Ask for the billing_admin role or platform admin access in rule42-auth.');
      }
      setSession(nextSession);
      return returnTo;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [authConfig]);

  /**
   * logout — clears the local session and ends the OIDC browser session.
   *
   * @since 2026-05-31 (AT-50.01)
   */
  const logout = useCallback(async (): Promise<void> => {
    const currentSession = session;
    setSession(null);
    setUser(null);
    setError(null);
    await logoutFromOidc(authConfig, currentSession);
  }, [authConfig, session]);

  const value: AdminAuthState = {
    user,
    token,
    login,
    completeLogin,
    logout,
    loading,
    error,
    apiBaseUrl,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * useAdminAuth — returns the current admin auth state.
 *
 * Must be used inside AdminAuthProvider.
 *
 * @returns AdminAuthState with { user, token, login, logout, loading, error }
 * @throws {Error} If called outside AdminAuthProvider
 * @since 2026-05-31 (AT-50.01)
 */
export function useAdminAuth(): AdminAuthState {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return ctx;
}
